import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTokens } from '../../context/TokenContext';
import { apiClient } from '../../utils/apiClient';
import { useWebSocket } from '../../context/WebSocketContext';
import Bell from './../icons/Bell';
import NotificationList from './NotificationList';
import ProfilePictureManager from '../../layout/ProfilePictureManager';
import { formatTokenAmount } from '../../utils/tokenFormatter';

interface Notification {
  _id: string;
  message: string;
  status: 'unread' | 'read';
  createdAt: string;
  type: string;
  relatedEntityId?: string;
}

const UserProfile: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { balance } = useTokens();
  const { socket } = useWebSocket();
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalUnreadCount, setTotalUnreadCount] = useState(0);

  // Récupération du nombre de notifications non lues
  const fetchUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const response = await apiClient(`/notifications/${user.id}/unread-count`);
      const data = await response.json();
      setTotalUnreadCount(data.unreadCount);
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de notifications non lues:', error);
    }
  }, [user]);

  // Récupération des notifications
  const fetchNotifications = useCallback(async (page: number) => {
    if (!user) return;
    setIsLoading(true);
    try {
      const response = await apiClient(`/notifications/${user.id}?page=${page}&limit=10`);
      const data = await response.json();

      // Ne pas réinitialiser les notifications sauf si c'est la première page
      setNotifications(prevNotifications =>
        page === 1 ? data.notifications : [...prevNotifications, ...data.notifications]
      );
      setHasMore(data.currentPage < data.totalPages);
      setCurrentPage(data.currentPage); // Mettre à jour la page actuelle
    } catch (error) {
      console.error('Erreur lors de la récupération des notifications:', error);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Marquer une notification comme lue
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await apiClient(`/notifications/${notificationId}/read`, {
        method: 'PATCH',
      });

      setNotifications(prevNotifications =>
        prevNotifications.map(notification =>
          notification._id === notificationId
            ? { ...notification, status: 'read' }
            : notification
        )
      );
      setTotalUnreadCount(prevCount => Math.max(prevCount - 1, 0));
    } catch (error) {
      console.error('Erreur lors de la mise à jour de la notification comme lue:', error);
    }
  }, []);

  useEffect(() => {
    if (user && socket) {
      fetchUnreadCount();
      fetchNotifications(1); // Charger la première page

      socket.emit('authenticate', user.id);

      const handleNewNotification = (notificationData: Notification) => {
        setNotifications(prevNotifications => [notificationData, ...prevNotifications]);
        fetchUnreadCount();
        if (showNotifications) {
          fetchNotifications(1); // Rafraîchir les notifications si le panneau est ouvert
        }
      };

      socket.on('newNotification', handleNewNotification);

      return () => {
        socket.off('newNotification', handleNewNotification);
      };
    }
  }, [user, socket, fetchNotifications, fetchUnreadCount, showNotifications]);

  const loadMore = useCallback(() => {
    if (hasMore && !isLoading) {
      fetchNotifications(currentPage + 1); // Charger la page suivante
    }
  }, [hasMore, isLoading, currentPage, fetchNotifications]);

  if (!user) {
    return null;
  }

  return (
    <div className="relative py-2 px-4 border-t border-mcdm-primary-200 dark:border-mcdm-third-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <div
            onClick={() => navigate('/mon-profil')}
            className="cursor-pointer hover:opacity-80 transition-opacity duration-200"
            title="Voir mon profil"
          >
            <ProfilePictureManager
              size="sm"
              className="mr-2 border border-mcdm-third-500 dark:border-mcdm-third-100 rounded-full"
            />
          </div>
          <div className="flex flex-col">
            <span className="text-sm text-mcdm-third-500 dark:text-mcdm-primary-100">{user.name}</span>
            <span className="text-xs text-mcdm-third-400 dark:text-mcdm-primary-300">
              {formatTokenAmount(balance)} token{balance > 1 ? 's' : ''}
            </span>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => {
              setShowNotifications(!showNotifications);
              if (!showNotifications) {
                fetchNotifications(1);
              }
            }}
            className="relative dark:text-mcdm-primary-100 text-mcdm-third-500"
          >
            <Bell stroke="currentColor" fill="none" width="24px" height="24px" />
            {totalUnreadCount > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-4 w-4 flex items-center justify-center">
                {totalUnreadCount}
              </span>
            )}
          </button>

          {showNotifications && (
            <div className="absolute right-0 bottom-[30px] mb-3 w-[225px]">
              <NotificationList
                notifications={notifications}
                isLoading={isLoading}
                hasMore={hasMore}
                loadMore={loadMore}
                markAsRead={markAsRead}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;